import { useMemo } from 'react';

import {
  STORE_LOCATOR_DELIVERY_HREF,
  STORE_LOCATOR_PICKUP_HREF,
} from '@fhs/store-locator/src/constants';
import { useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import {
  isDelivery as _isDelivery,
  isPickup as _isPickup,
  isDriveThru,
} from '@fhs-legacy/frontend/src/utils/service-mode';

import {
  CAREERS_PATH,
  CATERING_PATH,
  DONATE_PATH,
  FOUNDATION_PATH,
  FRANCHISE_PATH,
} from './constants';

type ServiceModeSelection = {
  noStoreSelected: boolean;
  isSelectedMode: boolean;
  enableStoreLocatorV2: boolean;
};

type ServiceModeHrefParams = ServiceModeSelection & {
  storeLocatorV2Href: string;
  legacyStoreLocatorHref: string;
};

function getServiceModeHref({
  noStoreSelected,
  isSelectedMode,
  enableStoreLocatorV2,
  storeLocatorV2Href,
  legacyStoreLocatorHref,
}: ServiceModeHrefParams) {
  if (!noStoreSelected && isSelectedMode) {
    return '/menu';
  }

  if (enableStoreLocatorV2) {
    return storeLocatorV2Href;
  }

  return legacyStoreLocatorHref;
}

function getPickUpHref(params: ServiceModeSelection) {
  return getServiceModeHref({
    ...params,
    storeLocatorV2Href: STORE_LOCATOR_PICKUP_HREF,
    legacyStoreLocatorHref: '/store-locator/service-mode',
  });
}

function getDeliveryHref({
  hideDeliveryCta,
  ...params
}: ServiceModeSelection & { hideDeliveryCta: boolean }) {
  if (hideDeliveryCta) {
    return '';
  }

  return getServiceModeHref({
    ...params,
    storeLocatorV2Href: STORE_LOCATOR_DELIVERY_HREF,
    legacyStoreLocatorHref: '/store-locator/address',
  });
}

export function useHrefs() {
  const { noStoreSelected } = useStoreContext();
  const { serviceMode } = useServiceModeContext();
  const isDelivery = _isDelivery(serviceMode);
  const isPickup = _isPickup(serviceMode) || isDriveThru(serviceMode);
  const enableStoreLocatorV2 = useFlag('enable-store-locator-v2');
  const hideDeliveryCta = useFlag('temp-hide-delivery-cta');
  const pickUpHref = getPickUpHref({
    noStoreSelected,
    isSelectedMode: isPickup,
    enableStoreLocatorV2,
  });
  const deliveryHref = getDeliveryHref({
    hideDeliveryCta,
    noStoreSelected,
    isSelectedMode: isDelivery,
    enableStoreLocatorV2,
  });
  const cateringLink: string = useFlag('home-catering-url') || CATERING_PATH;
  const cateringHref = useFlag('enable-catering-cta') ? cateringLink : '';

  const defaultServiceModeHref = isDelivery ? deliveryHref : pickUpHref;

  return useMemo(
    () => ({
      pickUpHref,
      deliveryHref,
      cateringHref,
      defaultServiceModeHref,
      foundationHref: FOUNDATION_PATH,
      careersHref: CAREERS_PATH,
      franchiseHref: FRANCHISE_PATH,
      donateHref: DONATE_PATH,
    }),
    [pickUpHref, deliveryHref, cateringHref]
  );
}
